import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import { Customer, System, Ticket, User } from 'src/app/models';
import { TcSingleTicketPageUtilsService, TcTicketService } from 'src/app/shared/services';
import { ToastService } from '@teorema/patron';
import { Observable, Subject } from 'rxjs';
import { constants } from '@app/shared/utils/constants';

@Component({
  selector: 'ts-main-tab',
  templateUrl: './ts-main-tab.component.html',
  styleUrls: ['./ts-main-tab.component.scss'],
  standalone: false,
})
export class TsMainTabComponent implements OnInit, AfterViewInit {
  @Input() unconfirmedTicket?: Ticket;
  @Input() defaultPageSize;
  @Input() attachments?: any[];
  @Input() taskId;
  @Input() userResp?: User;
  @Input() userDes?: User;
  @Input() users?: Observable<User[]>;
  @Input() usersInput = new Subject<string>();
  @Input() loadingUsers = false;
  @Input() user?: User;

  @Output() updateUserDes = new EventEmitter<User>();
  @Output() updateUserResp = new EventEmitter<User>();
  @Output() saved = new EventEmitter<boolean>();
  @Output() saving = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<any>();

  @ViewChild('form') private form?: NgForm;
  formEditTicket!: FormGroup;
  ticketsSituation = constants.ticketcenter.ticketsSituation;
  problemViewMode: 'markdown' | 'text' = 'markdown';
  problemWidthLevel: 1 | 2 | 3 = 3;
  private readonly problemViewModeStorageKey = 'tsProblemViewMode';
  private readonly problemWidthLevelStorageKey = 'tsProblemWidthLevel';

  constructor(
    private singleTicketPageUtilsService: TcSingleTicketPageUtilsService,
    private formBuilder: FormBuilder,
    public tcTicketService: TcTicketService,
    private toastService: ToastService
  ) {
    this.formEditTicket = this.formBuilder.group({
      situation: ['', Validators.required],
      clientTicket: ['', Validators.required],
      ticketStatus: ['', Validators.required],
      ticketSystem: ['', Validators.required],
      responsible: ['', Validators.required],
      devTicket: [''],
      urgentTicket: [false],
      priorityTicket: [''],
    });
  }

  setProblemViewMode(mode: 'markdown' | 'text') {
    if (this.problemViewMode === mode) return;
    this.problemViewMode = mode;
    this.persistProblemViewMode();
  }

  get showProblemAsMarkdown(): boolean {
    return this.problemViewMode === 'markdown';
  }

  setProblemWidthLevel(level: 1 | 2 | 3) {
    if (this.problemWidthLevel === level) return;
    this.problemWidthLevel = level;
    this.persistProblemWidthLevel();
  }

  get problemWidthClass(): string {
    return `ticket-problem-wrapper level-${this.problemWidthLevel}`;
  }
  ngAfterViewInit() {}

  ngOnInit() {
    this.loadProblemPreferences();
  }

  customers?: Observable<Customer[]>;
  customersInput = new Subject<string>();
  loadingCustomers = false;

  systems?: Observable<System[]>;
  systemsInput = new Subject<string>();
  loadingSystem = false;

  isFormValid() {
    return this.formEditTicket.valid;
  }

  mountFormGroupMainTab(unconfirmedTicketParam: Ticket) {
    this.unconfirmedTicket = unconfirmedTicketParam;

    if (this.unconfirmedTicket?.situation) {
      let temp = this.ticketsSituation.find(ts => ts.value === this.unconfirmedTicket?.situation);

      if (temp) {
        this.formEditTicket.get('situation')?.setValue(temp);
      }
    }

    this.formEditTicket.get('clientTicket')?.setValue(this.unconfirmedTicket?.customer);
    this.formEditTicket.get('ticketStatus')?.setValue(this.unconfirmedTicket?.status);
    this.formEditTicket.get('ticketSystem')?.setValue(this.unconfirmedTicket?.system);
    this.formEditTicket.get('responsible')?.setValue(this.unconfirmedTicket?.responsible);
    this.formEditTicket.get('devTicket')?.setValue(this.unconfirmedTicket?.developer);
    this.formEditTicket.get('priorityTicket')?.setValue(this.unconfirmedTicket?.priority);
    this.formEditTicket.get('urgentTicket')?.setValue(this.unconfirmedTicket?.urgent);
  }

  getTicketForm(): FormGroup {
    return this.formEditTicket;
  }

  private loadProblemPreferences(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const storedViewMode = window.localStorage.getItem(this.problemViewModeStorageKey) as
      | 'markdown'
      | 'text'
      | null;
    if (storedViewMode === 'markdown' || storedViewMode === 'text') {
      this.problemViewMode = storedViewMode;
    }

    const storedWidthLevel = window.localStorage.getItem(this.problemWidthLevelStorageKey);
    const widthNumber = storedWidthLevel ? Number.parseInt(storedWidthLevel, 10) : undefined;
    if (widthNumber === 1 || widthNumber === 2 || widthNumber === 3) {
      this.problemWidthLevel = widthNumber;
    }
  }

  private persistProblemViewMode(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(this.problemViewModeStorageKey, this.problemViewMode);
  }

  private persistProblemWidthLevel(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(
      this.problemWidthLevelStorageKey,
      this.problemWidthLevel.toString()
    );
  }

  prepareToSave() {
    let situacao = this.formEditTicket.get('situation')?.value;
    this.unconfirmedTicket!.situation = situacao?.value;
    this.unconfirmedTicket!.customer = this.formEditTicket.get('clientTicket')?.value;
    this.unconfirmedTicket!.status = this.formEditTicket.get('ticketStatus')?.value;
    this.unconfirmedTicket!.system = this.formEditTicket.get('ticketSystem')?.value;
    this.unconfirmedTicket!.responsible = this.formEditTicket.get('responsible')?.value;
    this.unconfirmedTicket!.developer = this.formEditTicket.get('devTicket')?.value;
    this.unconfirmedTicket!.priority = this.formEditTicket.get('priorityTicket')?.value;
    this.unconfirmedTicket!.urgent = this.formEditTicket.get('urgentTicket')?.value;
  }

  onChangeSituation() {
    let situation = this.formEditTicket.get('situation')?.value;

    if (
      this.unconfirmedTicket &&
      this.unconfirmedTicket?.type === 'L' &&
      situation &&
      situation?.value === 'F'
    ) {
      const open = this.ticketsSituation.find(item => item.value === 'A');
      setTimeout(() => {
        this.formEditTicket.get('situation')?.setValue(open);
        situation = this.formEditTicket.get('situation');
        this.unconfirmedTicket!.situation = situation?.value;
      });
      this.toastService.show({
        color: 'warn',
        message: 'Chamado com liberação pendente. Devolva ao setor responsável.',
        duration: 15000,
      });
    }

    if (
      this.unconfirmedTicket &&
      this.unconfirmedTicket?.release_situation === 'P' &&
      situation &&
      situation?.value === 'F'
    ) {
      const open = this.ticketsSituation.find(item => item.value === 'A');
      setTimeout(() => {
        this.formEditTicket.get('situation')?.setValue(open);
        situation = this.formEditTicket.get('situation');
        this.unconfirmedTicket!.situation = situation?.value;
      });
      this.toastService.show({
        color: 'warn',
        message: 'Chamado com liberação de release pendente. Devolva ao desenvolvimento.',
        duration: 15000,
      });
    }
  }
}
